import { fabric } from 'fabric';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';

import { ItemModel } from 'src/app/models/itemModel';
import { FabricObjectSettings, LINE_TYPE } from '../../context-menus/fabric-object-settings';
import {
  RhombusPolygon,
  TrapezoidPolygon,
  PentagonPolygon,
  HexagonPolygon,
  InclinedRhombusPolygon,
  OctagonPolygon,
  StarPolygon,
  SpecialRightTrianglePolygon,
  RightTrianglePolygon,
  AppCustomPolygon,
} from '../../wb-toolbar/toolbars/fabric-polygons';
import { BoardItemsManager } from '../../items-canvas/board-items/board-items-manager';
import { BoardLine } from '../../items-canvas/board-items/board-line';

export enum Shapes {
  LINE = 'line',
  DASHED_LINE = 'dashed_line',
  ARROW = 'arrow',
  RECTANGLE = 'rectangle',
  ROUNDED_RECTANGLE = 'rounded-rectangle',
  CIRCLE = 'circle',
  TRIANGLE = 'triangle',
  RHOMBUS = 'rhombus',
  SPECIAL_RIGHT_TRIANGLE = 'special_right_triangle',
  RIGHT_TRIANGLE = 'right_triangle',
  INCLINED_RHOMBUS = 'inclined_rhombus',
  TRAPEZOID = 'trapezoid',
  STAR = 'star',
  PENTAGON = 'pentagon',
  HEXAGON = 'hexagon',
  OCTAGON = 'octagon',
}

export enum FabricShapeType {
  POLYGON = 'polygon',
  ELLIPSE = 'ellipse',
}

export class ShapesFactory {
  constructor(
    private sharedDataService: SessionSharedDataService,
    private shapeSettings: FabricObjectSettings,
    private lineSettings: FabricObjectSettings,
  ) {}

  createShapeFromEvent(
    pointer: { x: number; y: number },
    activeShape: Shapes,
    selectedObject?: fabric.Object,
  ): fabric.Object | undefined {
    if (selectedObject) {
      selectedObject.hoverCursor = 'crosshair';
    }
    switch (activeShape) {
      case Shapes.LINE:
        return this.createBoardLineShape(pointer, LINE_TYPE.LINE);
      case Shapes.ARROW:
        return this.createBoardLineShape(pointer, LINE_TYPE.ARROW);
      case Shapes.DASHED_LINE:
        return this.createBoardLineShape(pointer, LINE_TYPE.DASHED);
      case Shapes.RECTANGLE:
        return this.createShape(fabric.Rect, {
          pointer,
        });
      case Shapes.CIRCLE:
        return this.createShape(fabric.Ellipse, {
          pointer,
        });
      case Shapes.ROUNDED_RECTANGLE:
        return this.createShape(fabric.Rect, {
          pointer,
          rounded: true,
        });
      case Shapes.TRIANGLE:
        return this.createShape(fabric.Triangle, {
          pointer,
        });
      case Shapes.STAR:
        return this.createPolygonInstance(StarPolygon, pointer);
      case Shapes.INCLINED_RHOMBUS:
        return this.createPolygonInstance(InclinedRhombusPolygon, pointer);
      case Shapes.OCTAGON:
        return this.createPolygonInstance(OctagonPolygon, pointer);
      case Shapes.HEXAGON:
        return this.createPolygonInstance(HexagonPolygon, pointer);
      case Shapes.PENTAGON:
        return this.createPolygonInstance(PentagonPolygon, pointer);
      case Shapes.TRAPEZOID:
        return this.createPolygonInstance(TrapezoidPolygon, pointer);
      case Shapes.RHOMBUS:
        return this.createPolygonInstance(RhombusPolygon, pointer);
      case Shapes.SPECIAL_RIGHT_TRIANGLE:
        return this.createPolygonInstance(SpecialRightTrianglePolygon, pointer);
      case Shapes.RIGHT_TRIANGLE:
        return this.createPolygonInstance(RightTrianglePolygon, pointer);
    }
  }

  createBoardLineShape(pointer: { x: number; y: number }, lineType: LINE_TYPE): BoardLine {
    const boardLine = BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Line>(
      ItemModel.Line,
      {
        top: pointer.y,
        left: pointer.x,
        startPoint: new fabric.Point(pointer.x, pointer.y),
        endPoint: new fabric.Point(pointer.x, pointer.y),
        strokeColor: this.lineSettings.strokeColor,
        strokeWidth: this.lineSettings.strokeWidth,
        type: lineType,
      },
      undefined,
      true,
    );
    return boardLine as BoardLine;
  }

  createShape(
    classRef: { new (arg: fabric.IRectOptions | fabric.ITriangleOptions | fabric.IEllipseOptions) },
    options: {
      pointer: { x: number; y: number };
      rounded?: boolean;
    },
  ): fabric.Object {
    const shape: fabric.Object = new classRef({
      top: options.pointer.y,
      left: options.pointer.x,
      width: 0,
      height: 0,
      stroke: this.shapeSettings.strokeColor,
      strokeWidth: this.shapeSettings.strokeWidth,
      fill: 'transparent',
      hasControls: false,
      excludeFromExport: true,
      perPixelTargetFind: true,
    });

    if ((options.rounded && shape instanceof fabric.Rect) || shape instanceof fabric.Ellipse) {
      shape.set({
        rx: shape instanceof fabric.Rect ? 10 : (shape.width as number) / 2,
        ry: shape instanceof fabric.Rect ? 10 : (shape.width as number) / 2,
      });

      if (shape instanceof fabric.Ellipse) {
        shape.set({
          originX: 'left',
          originY: 'top',
        });
      }
    }
    this.sharedDataService?.fabricCanvas?.add(shape);
    this.sharedDataService?.fabricCanvas?.setActiveObject(shape);
    return shape;
  }

  createPolygonInstance(
    classRef: { new (arg: fabric.IPolylineOptions) },
    pointer: { x: number; y: number },
  ): fabric.Object {
    const shape = new classRef({
      top: pointer.y,
      left: pointer.x,
      width: 0,
      height: 0,
      stroke: this.shapeSettings.strokeColor,
      strokeWidth: this.shapeSettings.strokeWidth,
      fill: 'transparent',
      hasControls: false,
      excludeFromExport: true,
    });

    this.sharedDataService.fabricCanvas?.add(shape);
    this.sharedDataService.fabricCanvas?.setActiveObject(shape);

    return shape;
  }

  handleBoardLineShapeMouseMove(
    activeObj: BoardLine,
    pointer: fabric.Point,
    holdControlKey: boolean,
  ): void {
    const lineBoardItem = activeObj as BoardLine;
    if (!holdControlKey) {
      const newEndPoint = new fabric.Point(pointer.x, pointer.y);
      lineBoardItem.configurations.endPoint = newEndPoint;
    } else {
      const snappedPoint = lineBoardItem.getSnappedPoint(
        pointer.x,
        pointer.y,
        lineBoardItem.configurations.startPoint,
      );
      lineBoardItem.configurations.endPoint = snappedPoint;
    }
    lineBoardItem.rebuildPath();
  }

  handleEllipseMouseMove(
    activeObj,
    pointer,
    origX: number,
    origY: number,
    holdControlKey: boolean,
  ): void {
    if (Number.isFinite(activeObj.top) && Number.isFinite(activeObj.left)) {
      if (origX > pointer.x) {
        activeObj.set({ left: pointer.x });
      }
      if (origY > pointer.y) {
        activeObj.set({ top: pointer.y });
      }
      const height = Math.abs(pointer.y - origY);
      const width = holdControlKey ? height : Math.abs(pointer.x - origX);
      if (holdControlKey && origX > pointer.x) {
        activeObj.set({ left: pointer.x - (width - Math.abs(pointer.x - origX)) });
      }
      (activeObj as fabric.Ellipse).set({
        ry: height / 2,
        rx: width / 2,
        height: height,
        width: width,
      });
      activeObj.setCoords();
    }
  }

  handlePolygonMouseMove(
    activeObj,
    pointer,
    origX: number,
    origY: number,
    holdControlKey: boolean,
  ): void {
    if (Number.isFinite(activeObj.top) && Number.isFinite(activeObj.left)) {
      (activeObj as AppCustomPolygon).refresh(
        {
          x: origX,
          y: origY,
        },
        pointer,
        holdControlKey,
      );
    }
  }

  handleDefaultMouseMove(
    activeObj,
    pointer,
    origX: number,
    origY: number,
    holdControlKey: boolean,
  ): void {
    if (Number.isFinite(activeObj.top) && Number.isFinite(activeObj.left)) {
      if (origX > pointer.x) {
        activeObj.set({ left: pointer.x });
      }
      if (origY > pointer.y) {
        activeObj.set({ top: pointer.y });
      }
      const height = Math.abs(pointer.y - origY);
      const width = holdControlKey ? height : Math.abs(pointer.x - origX);
      if (holdControlKey && origX > pointer.x) {
        activeObj.set({ left: pointer.x - (width - Math.abs(pointer.x - origX)) });
      }
      activeObj.set({ height: height, width: width });
      activeObj.setCoords();
    }
  }
}
